/**
 * ActiveScheduleBanner Component
 * 
 * Sticky Banner wenn ein Schedule aktiv ist.
 * Zeigt Countdown, Version-Info und Cancel-Action.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { PublishingSchedule } from '@/types/publishing'
import { useTimeRemaining, getScheduleDisplayInfo } from '@/hooks/useActiveSchedule'
import { formatScheduleDate } from '@/hooks/usePublishSchedule'

interface ActiveScheduleBannerProps {
  schedule: PublishingSchedule
  onViewDetails?: () => void
  onCancel?: () => void
  isCanceling?: boolean
}

export function ActiveScheduleBanner({
  schedule,
  onViewDetails,
  onCancel,
  isCanceling = false,
}: ActiveScheduleBannerProps) {
  const { t } = useTranslation()
  const timeRemaining = useTimeRemaining(schedule.publish_end_at)
  const displayInfo = getScheduleDisplayInfo(schedule)
  
  // Update countdown every second
  const [, setTick] = useState(0)
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(timer)
  }, [])

  // Don't show if not active
  if (!displayInfo.isActive) {
    return null
  }

  return (
    <div className="schedule-active-banner alert alert-warning d-flex align-items-center mb-4">
      {/* Pulse Indicator */}
      <div className="schedule-pulse me-3">
        <i className="bi bi-clock-history fs-4" />
      </div>

      {/* Info */}
      <div className="flex-grow-1">
        <div className="fw-bold">
          <i className="bi bi-exclamation-triangle me-1" />
          {t('publishing.temporaryVersionActive')}
        </div>
        <div className="small">
          {schedule.version?.diff_summary ? (
            <>"{schedule.version.diff_summary}"</>
          ) : (
            <>V{schedule.version?.version_number}</>
          )}
          <span className="mx-2">•</span>
          <span className="text-dark">
            Endet {timeRemaining.formatted}
          </span>
          <span className="mx-2">•</span>
          <span className="text-muted">
            {formatScheduleDate(schedule.publish_end_at)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2">
        {onViewDetails && (
          <button 
            className="btn btn-sm btn-outline-dark"
            onClick={onViewDetails}
          >
            <i className="bi bi-info-circle me-1" />
            Details
          </button>
        )}
        {onCancel && (
          <button 
            className="btn btn-sm btn-outline-danger"
            onClick={onCancel}
            disabled={isCanceling}
          >
            {isCanceling ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <i className="bi bi-x-lg" />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Scheduled (not yet active) Banner
 */
interface ScheduledBannerProps {
  schedule: PublishingSchedule
  onViewDetails?: () => void
  onCancel?: () => void
  isCanceling?: boolean
}

export function ScheduledBanner({
  schedule,
  onViewDetails,
  onCancel,
  isCanceling = false,
}: ScheduledBannerProps) {
  const { t } = useTranslation()
  const displayInfo = getScheduleDisplayInfo(schedule)
  
  // Don't show if not scheduled
  if (!displayInfo.isScheduled) {
    return null
  }

  // Calculate time until start
  const now = new Date()
  const startDate = new Date(schedule.publish_start_at)
  const diffMs = startDate.getTime() - now.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  
  let startsIn = ''
  if (diffDays > 0) {
    startsIn = `in ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`
  } else if (diffHours > 0) {
    startsIn = `in ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`
  } else {
    startsIn = t('publishing.soonLabel')
  }

  return (
    <div className="schedule-pending-banner alert alert-info d-flex align-items-center mb-4">
      {/* Icon */}
      <div className="me-3">
        <i className="bi bi-calendar-event fs-4" />
      </div>

      {/* Info */}
      <div className="flex-grow-1">
        <div className="fw-bold">
          Geplante Version
        </div>
        <div className="small">
          {schedule.version?.diff_summary ? (
            <>"{schedule.version.diff_summary}"</>
          ) : (
            <>V{schedule.version?.version_number}</>
          )}
          <span className="mx-2">•</span>
          <span className="text-dark">
            Startet {startsIn}
          </span>
          <span className="mx-2">•</span>
          <span className="text-muted">
            {formatScheduleDate(schedule.publish_start_at)} – {formatScheduleDate(schedule.publish_end_at)}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2">
        {onViewDetails && (
          <button 
            className="btn btn-sm btn-outline-secondary"
            onClick={onViewDetails}
          >
            <i className="bi bi-info-circle me-1" />
            Details
          </button>
        )}
        {onCancel && (
          <button 
            className="btn btn-sm btn-outline-danger"
            onClick={onCancel}
            disabled={isCanceling}
          >
            {isCanceling ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-x-lg me-1" />
                {t('common.cancel')}
</>
            )}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Combined Banner that shows either Active or Scheduled
 */
interface ScheduleBannerProps {
  schedule: PublishingSchedule | null | undefined
  onViewDetails?: () => void
  onCancel?: () => void
  isCanceling?: boolean
}

export function ScheduleBanner({
  schedule,
  onViewDetails,
  onCancel,
  isCanceling = false,
}: ScheduleBannerProps) {
  if (!schedule) {
    return null
  }

  if (schedule.status === 'active') {
    return (
      <ActiveScheduleBanner
        schedule={schedule}
        onViewDetails={onViewDetails}
        onCancel={onCancel}
        isCanceling={isCanceling}
      />
    )
  }

  if (schedule.status === 'scheduled') {
    return (
      <ScheduledBanner
        schedule={schedule}
        onViewDetails={onViewDetails}
        onCancel={onCancel}
        isCanceling={isCanceling}
      />
    )
  }

  return null
}


